/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';

import { getDocumentDownloadUrlAsync } from './DataService/DataActionExtension.g';
import { IGetDocumentDownloadUrlResponse } from './DataService/DataServiceEntities.g';

/**
 * Symbole de l'énumération X++ BOADocumentType. Indépendant de la langue.
 */
export const DocumentTypeInvoice = 'Invoice';

/**
 * GetDocumentDownloadUrl Input Action.
 * Le numéro de compte client n'est pas transmis : il est résolu côté CSU depuis la
 * session authentifiée.
 */
export class GetDocumentDownloadUrlInput implements IActionInput {
    public documentType: string;
    public documentId: string;

    constructor(documentId: string, documentType: string = DocumentTypeInvoice) {
        this.documentId = documentId;
        this.documentType = documentType;
    }

    public getCacheKey = () => `GetDocumentDownloadUrl-${this.documentType}-${this.documentId}`;
    public getCacheObjectType = () => 'GetDocumentDownloadUrlResponse';

    // Aucune mise en cache : le lien est signé et expire après 15 minutes. Un lien
    // recyclé depuis le cache renverrait un 403 au deuxième téléchargement.
    public dataCacheType = (): CacheType => 'none';
}

/**
 * Obtient un lien de téléchargement temporaire pour un document.
 * L'action ne lève pas d'exception : la demande exige d'afficher un message à l'usager
 * plutôt qu'une erreur, l'appelant se fie donc uniquement à Success et Status.
 */
export async function getDocumentDownloadUrl(
    input: GetDocumentDownloadUrlInput,
    ctx: IActionContext
): Promise<IGetDocumentDownloadUrlResponse> {
    if (!input.documentId) {
        return { Success: false, Status: 'Error', ErrorMessage: 'Document id is required' };
    }

    return getDocumentDownloadUrlAsync({ callerContext: ctx }, input.documentType, input.documentId)
        .then(data => {
            return data;
        })
        .catch(error => {
            ctx.telemetry.exception(error);

            // Ni l'URL ni le contenu du document ne sont tracés.
            ctx.trace(`[get-document-download-url] Unable to get a download link for document ${input.documentId}`);

            return { Success: false, Status: 'Error', ErrorMessage: 'Unable to get a download link' };
        });
}

const createInput = (args: ICreateActionContext, documentId: string, documentType?: string): IActionInput => {
    return new GetDocumentDownloadUrlInput(documentId, documentType);
};

export default createObservableDataAction({
    action: <IAction<IGetDocumentDownloadUrlResponse>>getDocumentDownloadUrl,
    id: 'GetDocumentDownloadUrl',
    input: createInput
});
